import { Button } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

// Components
import Billing from "../steps/Billing";
import Services from "../steps/Services";

// Providers
import { useClaim } from "../../../context-providers/claims-provider";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";

// Types
import CustomModal from "../../../shared/Modals/CustomModal";
import { ClaimService, User } from "../../../utils/types";

// Utils
import { validateClaimsBilling } from "../../../utils/api/claims";

interface ServiceBillingEditModalProps {
	isOpen: boolean;
	onClose(): void;
	onContinue(servicesChanged: boolean): void;
}

const normalizeServicesForFormRegeneration = (services: ClaimService[] = []) =>
	services.map((service) => ({
		serviceId: service?.serviceId,
		legalBenefitId: service?.legalBenefitId,
		serviceProvidedFor: service?.serviceProvidedFor,
		recipientAge: service?.recipientAge,
		finalDisposition: service?.finalDisposition,
		consultationCompleted: service?.consultationCompleted,
		officeWorkCompleted: service?.officeWorkCompleted,
		negotiationCorrespondenceCompleted: service?.negotiationCorrespondenceCompleted,
		lawsuitCaseFiledRespondedCompleted: service?.lawsuitCaseFiledRespondedCompleted,
		courtAppearanceCompleted: service?.courtAppearanceCompleted,
		caseFullyCompleted: service?.caseFullyCompleted
	}));

export default function ServiceBillingEditModal({ isOpen, onClose, onContinue }: ServiceBillingEditModalProps) {
	const { id } = useParams();
	const toast = useClaimsToast();

	const { claim, localClaim, localBenefits, billHourlyAsFlat, setLocalClaim, handleFetchClaim, handleClaimUpsert } =
		useClaim();
	const { getUser } = useAuth();
	const currentUser: User | undefined = getUser();

	useEffect(() => {
		// If claim is missing or is missing needed service info, fetch the claim
		if (!claim || !claim.claimServices || !claim.claimServices.length) {
			handleFetchClaim(+(id ?? "0"));
		}
	}, []);

	const validationError = useMemo(() => {
		if (currentUser) return validateClaimsBilling(localClaim, localBenefits, currentUser, billHourlyAsFlat);
	}, [localClaim, localBenefits, billHourlyAsFlat, currentUser]);

	const handleSubmitUpdate = async () => {
		const didServicesChange =
			JSON.stringify(normalizeServicesForFormRegeneration(claim?.claimServices)) !==
			JSON.stringify(normalizeServicesForFormRegeneration(localClaim?.claimServices));
		const isSuccess = await handleClaimUpsert(localClaim, true);
		if (isSuccess) {
			toast(undefined, "Services & Billing updated successfully", "success");
			onContinue(didServicesChange);
		}
	};

	return isOpen ? (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleSubmitUpdate}
			size="3xl"
			title="Edit Services & Billing"
			continueText="Update"
			continueDisabled={!!validationError}
			continueTooltip={validationError}
			content={
				<>
					<Services
						isInAdminMode={true}
						currentStep={0}
						handleStepPrevious={() => {
							// Does nothing
						}}
						handleStepClick={() => {
							// Does nothing
						}}
					/>
					<Billing
						isInAdminMode={true}
						handleStepPrevious={() => {
							// Does nothing
						}}
						handleStepClick={() => {
							// Does nothing
						}}
						currentStep={0}
					/>
				</>
			}
			footerContent={
				<Button variant="ghost" mr="8" onClick={() => setLocalClaim(claim)}>
					Reset
				</Button>
			}
		/>
	) : (
		<></>
	);
}
